"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { X } from "lucide-react";

const VORKASSE_OPEN_ORDER_COOKIE = "vorkasse_open_order";

function readCookie(): { number: number; days: number; orderId: string } | null {
	const raw = document.cookie
		.split("; ")
		.find((c) => c.startsWith(`${VORKASSE_OPEN_ORDER_COOKIE}=`))
		?.slice(VORKASSE_OPEN_ORDER_COOKIE.length + 1);
	if (!raw) return null;
	try {
		const parsed = JSON.parse(decodeURIComponent(raw)) as { n?: number; d?: string; o?: string };
		if (!parsed.n || !parsed.d || !parsed.o) return null;
		const days = Math.max(0, Math.ceil((new Date(parsed.d).getTime() - Date.now()) / 86_400_000));
		return { number: parsed.n, days, orderId: parsed.o };
	} catch {
		return null;
	}
}

/**
 * Dismissible storefront bar for a customer with an open transfer (§3.4). The
 * cookie holds only the order number, the deadline and the Saleor GID, never
 * an amount or an address. Reading it in an effect keeps the server render
 * identical for everyone, so no hydration mismatch and no cache poisoning.
 */
export function OpenTransferBar({ locale }: { locale: string }) {
	const t = useTranslations("vorkasse");
	const [state, setState] = useState<ReturnType<typeof readCookie>>(null);

	useEffect(() => {
		// eslint-disable-next-line react-hooks/set-state-in-effect -- hydration-safe cookie read: `document.cookie` does not exist on the server, so this cannot become a lazy useState initialiser; the server render and the first client render must both show nothing, and the cookie value fills in right after
		setState(readCookie());
	}, []);

	if (!state) return null;

	return (
		<div className="bg-info/40 border-b border-info-border px-4 py-2 text-info-foreground">
			<div className="mx-auto flex max-w-6xl items-center justify-between gap-3">
				<p className="text-sm">
					{t("openTransferBar", { number: state.number, days: state.days })}{" "}
					{/* The GID is base64 and contains "=", always encode it into a URL. */}
					<Link
						href={`/${locale}/vorkasse/${encodeURIComponent(state.orderId)}`}
						className="font-medium underline underline-offset-4"
					>
						{t("standaloneEyebrow")}
					</Link>
				</p>
				<button
					type="button"
					aria-label="Close"
					onClick={() => {
						document.cookie = `${VORKASSE_OPEN_ORDER_COOKIE}=; Max-Age=0; path=/`;
						setState(null);
					}}
					className="shrink-0"
				>
					<X className="h-4 w-4" />
				</button>
			</div>
		</div>
	);
}
